<script lang="ts">
	import { format } from 'svelte-i18n';
	//Types
	import type {
		ChartConfig,
		ChartStatisticInterface,
		ChartStatisticRangePeriod,
	} from 'src/types/Chart';
	import type { Device } from 'src/types/Device';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	//Stores
	import { user_data, user_settings } from 'src/stores';
	//Components
	import LayerChartStatistic from './LayerChartStatistic.svelte';
	import LayerChartCardSummary from './LayerChartCardSummary.svelte';
	import LayerChartSpacing from './LayerChartSpacing.svelte';
	import ChartTokenList from '../ChartTokenList.svelte';
	import {
		Modal,
		Text,
		LayerChartWrapper,
		LayerChartControls,
		ChartCard as LayerChartCard,
		ChartCardFullScreen as LayerChartCardFullScreen,
		type StatisticsData,
		type ChartVisualisation,
		type SplitChartType,
	} from '@Shelly/shelly-design-system/index';
	//Helpers
	import {
		buildChartControlsData,
		buildChartHeaderMenu,
		getInitLoadedPeriodStatistics,
		LAYER_RANGE_OPTIONS,
		type ChartMenuItem,
	} from 'src/helpers/statistics/ui/chart_control_helpers';
	import { getStatistics } from 'src/helpers/statistics/request/chart_get_statistics_helpers';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	import { getChartSensorName, getDeviceTimeZoneDate } from 'src/helpers/statistics/core/chart_helper';
	import { completeFullscreenTooltipData } from './fullscreen_chart_tooltip';
	import { isThreeEM } from 'src/helpers/interface_helpers';

	export let device: Device;

	const chartConfigs: ChartConfig[] = getDeviceChartsConfig(device)
		.map((i) => i.charts)
		.flat();

	const isPremiumUser = $user_data.premium || $user_settings.been_premium;
	const chartMenu: ChartMenuItem[] = buildChartHeaderMenu(chartConfigs);
	$: graphTokens = chartConfigs.map((_, index) => ({
		id: String(index),
		label: chartMenu[index].title,
	}));
	const timeZone = getDeviceTimeZoneDate(device);
	const normalizeDrawerStatistics = (request: Promise<StatisticsData>): Promise<StatisticsData> =>
		request.then((result) => ({
			...result,
			chartData: {
				...result.chartData,
				type: result.chartData.type === 'pie' ? 'pie' : 'area',
			},
		}));
	const requestStatistics = (showPreviousPeriod: boolean): Promise<StatisticsData> =>
		normalizeDrawerStatistics(
			getStatistics(
				device,
				selectedChartControlsData || buildChartControlsData(device, activePeriod),
				chartConfig,
				chartConfig.url,
				0,
				showPreviousPeriod,
			) as Promise<StatisticsData>,
		);
	const getPeriodTotal = (statistic: ChartStatisticInterface): string =>
		statistic?.totalConsumed ||
		statistic?.totalGenerated ||
		statistic?.totalCounter ||
		statistic?.average ||
		'';
	const getPeriodValueLabel = (statistic: ChartStatisticInterface): string =>
		statistic?.totalConsumedValue != null ||
		statistic?.totalGeneratedValue != null ||
		statistic?.totalCounter != null
			? $format('chart_period_total', { default: 'Period total' })
			: $format('chart_period_average', { default: 'Period average' });

	const onSelectPeriod = (
		currentPeriod: ChartStatisticRangePeriod,
		chartControlsData: ChartControlsDataInterface = null,
	) => {
		activePeriod = currentPeriod;
		const controlsData = chartControlsData || buildChartControlsData(device, currentPeriod);
		selectedChartControlsData = controlsData;
		statisticsData = normalizeDrawerStatistics(
			getStatistics(
				device,
				controlsData,
				chartConfig,
				chartConfig.url,
				0,
				showPreviousPeriod,
			) as Promise<StatisticsData>,
		);
		if (showFullScreen) {
			fullScreenStatisticsData = normalizeDrawerStatistics(
				getStatistics(
					device,
					controlsData,
					chartConfig,
					chartConfig.url,
					0,
					showPreviousPeriod,
				) as Promise<StatisticsData>,
			);
		}
	};

	const onControlsChange = (value: ChartControlsDataInterface) => {
		if (value?.['period'] != null) {
			const period = value['period'] as ChartStatisticRangePeriod;
			activePeriod = period;
			if (period !== 'custom') {
				onSelectPeriod(period);
			}
		} else if (value?.currentPeriod != null) {
			onSelectPeriod(value.currentPeriod as ChartStatisticRangePeriod, value);
		}
	};

	const onSelectChartType = (selectedIndex: number) => {
		selectedChartIndex = selectedIndex;
		chartConfig = chartConfigs[selectedChartIndex];
		onSelectPeriod(
			activePeriod,
			selectedChartControlsData?.currentPeriod === activePeriod ? selectedChartControlsData : null,
		);
	};

	const isThreePhaseConsumptionChart = (config: ChartConfig): boolean =>
		isThreeEM(device.type) && device?.mode != 'monophase' && config.name === 'consumption';

	let selectedChartIndex = 0;
	let chartConfig: ChartConfig = chartConfigs[selectedChartIndex];
	let selectedChartControlsData: ChartControlsDataInterface = null;
	let statisticsData: Promise<StatisticsData>;
	let fullScreenStatisticsData: Promise<StatisticsData>;
	const configuredInitialPeriod =
		getInitLoadedPeriodStatistics(LAYER_RANGE_OPTIONS).period || LAYER_RANGE_OPTIONS[1].period;
	let activePeriod: ChartStatisticRangePeriod =
		!isPremiumUser && configuredInitialPeriod === 'hour' ? '24_hours' : configuredInitialPeriod;
	let showFullScreen = false;
	let showPreviousPeriod = false;
	let visualisation: ChartVisualisation = 'stacked';
	let splitChartType: SplitChartType = 'bar';
	$: isThreePhaseConsumption = isThreePhaseConsumptionChart(chartConfig);
	const onPreviousPeriodChange = (show: boolean) => {
		showPreviousPeriod = show;
		onSelectPeriod(
			activePeriod,
			selectedChartControlsData?.currentPeriod === activePeriod ? selectedChartControlsData : null,
		);
	};
	const onVisualisationChange = (value: ChartVisualisation) => {
		visualisation = value;
	};
	const onSplitChartTypeChange = (type: SplitChartType) => {
		splitChartType = type;
		visualisation = 'split';
	};
	const withFullscreenChartType = (
		data: StatisticsData,
		useSplit: boolean,
		splitType: SplitChartType,
	): StatisticsData => {
		const chartData = completeFullscreenTooltipData(data.chartData);
		if (!useSplit) return { ...data, chartData };
		return { ...data, chartData: { ...chartData, type: splitType } };
	};
	const openFullScreen = () => {
		showFullScreen = true;
		fullScreenStatisticsData = requestStatistics(showPreviousPeriod);
	};

	onSelectPeriod(activePeriod);
</script>

<LayerChartControls
	{activePeriod}
	{isPremiumUser}
	{timeZone}
	chartControlID={`layer-main-${chartConfig.name}-${chartConfig.extid}`}
	onchange={onControlsChange}
/>
<div class:hour-period-chart={activePeriod === 'hour'}>
	<LayerChartWrapper
		{statisticsData}
		{chartMenu}
		{selectedChartIndex}
		class="mb-s"
		let:statisticsData
		onchange={onSelectChartType}
	>
		<div class="device-drawer-chart-card">
			<LayerChartCard
				chartMenu={[]}
				{statisticsData}
				{selectedChartIndex}
				legend
				latestValue={getPeriodTotal(statisticsData.chartStatistic)}
				valueLabel={getPeriodValueLabel(statisticsData.chartStatistic)}
				{showPreviousPeriod}
				{onPreviousPeriodChange}
				chooseGraphLabel={$format('chart_choose_graph')}
				manageGraphLabel={$format('chart_manage_graph')}
				previousPeriodLabel={$format('chart_display_previous_period')}
				showBottomLabels
				onFullscreen={openFullScreen}
				onchange={onSelectChartType}
				visualisation={isThreePhaseConsumption ? 'stacked' : undefined}
			/>
			<div class="device-drawer-chart-tokens">
				{#if graphTokens.length > 1}
					<div>
						<Text styleType="body-small" class="text-color-muted text-600">
							{$format('chart_choose_graph', { default: 'Choose graph' })}
						</Text>
						<ChartTokenList
							items={graphTokens}
							mode="single"
							selectedItemIds={[String(selectedChartIndex)]}
							wrap
							large
							on:select={(event) => onSelectChartType(Number(event.detail.id))}
						/>
					</div>
				{/if}
			</div>
		</div>
		<LayerChartCardSummary {statisticsData} />
	</LayerChartWrapper>
</div>

<Modal
	class="full-screen-chart-modal"
	on:close={() => (showFullScreen = false)}
	showModal={showFullScreen}
	removeScrollPadding
	title={chartConfig?.chartTitle || ''}
	hideSlotToolbar={!chartConfig?.sensorID}
	type="default"
>
	<Text slot="toolbar" styleType="body" noWrap>
		{getChartSensorName(chartConfig)}
	</Text>
	<div slot="content" class="ml-m mr-m">
		<LayerChartControls
			{activePeriod}
			{isPremiumUser}
			{timeZone}
			chartControlID={`layer-full-${chartConfig.name}-${chartConfig.extid}`}
			onchange={onControlsChange}
		/>
		<div class:hour-period-chart={activePeriod === 'hour'}>
			<LayerChartWrapper
				class="mb-s"
				statisticsData={fullScreenStatisticsData}
				{chartMenu}
				{selectedChartIndex}
				let:statisticsData={fullScreenData}
				onchange={onSelectChartType}
			>
				<div class="full-screen-chart-card">
					<LayerChartSpacing>
						<LayerChartCardFullScreen
							{chartMenu}
							statisticsData={withFullscreenChartType(
								fullScreenData,
								isThreePhaseConsumption && visualisation === 'split',
								splitChartType,
							)}
							{selectedChartIndex}
							latestValue={getPeriodTotal(fullScreenData.chartStatistic)}
							valueLabel={getPeriodValueLabel(fullScreenData.chartStatistic)}
							onchange={onSelectChartType}
							visualisation={isThreePhaseConsumption ? visualisation : undefined}
							onVisualisationChange={isThreePhaseConsumption
								? onVisualisationChange
								: null}
							{splitChartType}
							onChartTypeChange={isThreePhaseConsumption ? onSplitChartTypeChange : null}
							visualisationLabel={$format('chart_visualisation_label', {
								default: 'Visualisation',
							})}
							stackedLabel={$format('chart_visualisation_stacked', { default: 'Stacked' })}
							splitLabel={$format('chart_visualisation_split', { default: 'Split' })}
							chartTypeLabel={$format('chart_more_options_chart_type_label', {
								default: 'Chart type',
							})}
							barChartLabel={$format('chart_more_options_chart_type_bar_label', {
								default: 'Bar chart',
							})}
							areaChartLabel={$format('chart_more_options_chart_type_sharp_label', {
								default: 'Area chart',
							})}
							lineChartLabel={$format('chart_more_options_chart_type_line_label', {
								default: 'Line chart',
							})}
						/>
					</LayerChartSpacing>
				</div>
				<LayerChartStatistic chartStatistic={fullScreenData.chartStatistic} />
				{#if fullScreenData?.chartStatistic?.threeEMStatistic}
					{#each fullScreenData.chartStatistic.threeEMStatistic as phase}
						<LayerChartStatistic chartStatistic={phase} />
					{/each}
				{/if}
			</LayerChartWrapper>
		</div>
	</div>
</Modal>

<style>
	:global(.modal-container.full-screen-chart-modal.type-default) {
		background: black !important;
	}

	.hour-period-chart :global(.lc-axis-bottom-tick-label) {
		transform: rotate(-40deg);
		transform-box: fill-box;
		transform-origin: center;
	}

	.device-drawer-chart-card {
		position: relative;
		min-width: 0;
	}

	/* Detailed series, tariff, and returned-energy values belong to the full-screen chart. */
	.device-drawer-chart-card :global(.lc-card-tooltip) {
		display: none !important;
	}

	.full-screen-chart-card {
		position: relative;
	}

	.full-screen-chart-card :global(.lc-card.full-screen) {
		overflow: visible;
	}

	.device-drawer-chart-tokens {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		margin-top: var(--spacing-s);
		padding: 0 var(--spacing-xs);
	}

	.device-drawer-chart-tokens > div {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		min-width: 0;
	}

	@media (min-width: 768px) {
		:global(.modal-container.full-screen-chart-modal.type-default) {
			width: 80% !important;
			max-height: 90vh !important;
		}
	}
</style>
